<template>
  <v-app>
    <AppSidebar
      v-model:drawer="drawer"
      v-model:rail="rail"
      :isMobile="isMobile"
      :agents="agentStore.agents"
      :conversations="conversationStore.conversations"
      @new-chat="handleNewChat"
      @select-conversation="handleSelectConversation"
      @open-search="showSearchDialog = true"
      @go-home="handleGoHome"
    />

    <SearchChatsDialog
      v-model="showSearchDialog"
      :agents="agentStore.agents"
      @select-conversation="handleSelectConversation"
      @new-chat="handleNewChat"
    />

    <AppNavbar
      v-model:drawer="drawer"
      :isMobile="isMobile"
      :userName="userName"
      :userEmail="userEmail"
      :isAuthenticated="isAuthenticated"
      @logout="handleLogout"
      @go-home="handleGoHome"
      @select-agent="handleNewChat"
    />

    <v-main class="main-container">
      <v-container fluid class="pa-0 fill-height">
        <slot></slot>
      </v-container>
    </v-main>

    <!-- Global error/notification snackbar -->
    <v-snackbar
      v-model="notificationStore.show"
      :color="notificationStore.color"
      :timeout="4000"
    >
      <span role="status" aria-live="polite">{{
        notificationStore.message
      }}</span>
    </v-snackbar>
  </v-app>
</template>

<script setup lang="ts">
import AppNavbar from '@/components/layout/AppNavbar.vue';
import AppSidebar from '@/components/layout/AppSidebar.vue';
import SearchChatsDialog from '@/components/SearchChatsDialog.vue';
import {
  isAuthenticated as checkAuth,
  keycloak,
  logout,
} from '@/plugins/keycloak';
import { Agent } from '@/services/agentService';
import { type Conversation } from '@/services/conversationService';
import { useAgentStore } from '@/stores/agentStore';
import { useConversationStore } from '@/stores/conversationStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { storage } from '@/utils/localStorage';
import { onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';

const router = useRouter();
const agentStore = useAgentStore();
const conversationStore = useConversationStore();
const notificationStore = useNotificationStore();
const { mobile: isMobile } = useDisplay();

const drawer = ref(true);
const showSearchDialog = ref(false);
const rail = ref(storage.get<boolean>('sidebar-rail') || false);
const userName = ref('Guest');
const userEmail = ref('');
const isAuthenticated = ref(false);

watch(rail, (newValue) => {
  storage.set('sidebar-rail', newValue);
});

onMounted(async () => {
  isAuthenticated.value = Boolean(checkAuth());

  if (isAuthenticated.value && keycloak.tokenParsed) {
    userName.value =
      keycloak.tokenParsed.preferred_username ||
      keycloak.tokenParsed.name ||
      'User';
    userEmail.value = keycloak.tokenParsed.email || '';
  }

  // Load agents and conversations
  await Promise.all([
    agentStore.loadAgents(),
    conversationStore.loadConversations(),
  ]);
});

const handleSelectConversation = (conversation: Conversation) => {
  agentStore.setSelectedAgent(
    agentStore.getAgentById(conversation.agent.id) || null,
  );
  conversationStore.setSelectedConversation(conversation);
  router.push({
    path: `/en/echo/conversation/${conversation.id}`,
    query: { shouldReset: 'true' },
  });
  console.log('Selected conversation:', conversation);
};

const handleNewChat = (agent: Agent) => {
  agentStore.setSelectedAgent(agent);
  conversationStore.setAgentFilter(null);
  conversationStore.setSelectedConversation(null);
  router.push({
    path: '/en/echo/conversation',
    query: { shouldReset: 'true' },
  });
  console.log(
    'Starting new chat with agent:',
    agentStore.getSelectedAgent()?.name,
  );
};

const handleGoHome = () => {
  conversationStore.setSelectedConversation(null);
  router.push('/en/echo');
};

const handleLogout = () => {
  logout();
};
</script>

<style lang="css" scoped>
.main-container {
  background-color: #f9f9f9;
}
</style>
